// src/pages/ProfileDs.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  ListGroup,
  Row,
  Col,
  Alert,
  Button,
  Modal,
  Form,
  Tabs,
  Tab
} from "react-bootstrap";
import SchoolProfileProgress from "../SchoolProfileProgress";
import ChangePasswordCard from "../ChangePasswordCard";
import Swal from "sweetalert2";   // ✅ Added
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";

const ProfileDs = () => {
  const { user, updateUser } = useAuth();
  const { bearerAuth, publicAuth } = useApi();

  const [showEdit, setShowEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docSaving, setDocSaving] = useState(false);
  const [docError, setDocError] = useState("");
  const [timeSlots, setTimeSlots] = useState([]);
  const [workingHours, setWorkingHours] = useState([
    { day_of_week: "Monday", opening_time: "06:00:00", closing_time: "21:00:00", status: "open" },
    { day_of_week: "Tuesday", opening_time: "06:00:00", closing_time: "21:00:00", status: "open" },
    { day_of_week: "Wednesday", opening_time: "06:00:00", closing_time: "21:00:00", status: "open" },
    { day_of_week: "Thursday", opening_time: "06:00:00", closing_time: "21:00:00", status: "open" },
    { day_of_week: "Friday", opening_time: "06:00:00", closing_time: "21:00:00", status: "open" },
    { day_of_week: "Saturday", opening_time: "06:00:00", closing_time: "21:00:00", status: "open" },
    { day_of_week: "Sunday", opening_time: "00:00:00", closing_time: "00:00:00", status: "closed" }
  ]);
  const [applyToAll, setApplyToAll] = useState(false);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursError, setHoursError] = useState("");
  const [hoursConfigured, setHoursConfigured] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || "",
    city_id: user?.city_id || "",
    address: user?.address || "",
    zipcode: user?.zipcode || ""
  });

  const [docForm, setDocForm] = useState({
    bank_name: "",
    account_holder_name: "",
    account_number: "",
    ifsc: "",
    branch: "",
    id_proof_type: "",
    id_proof_number: ""
  });

  const [docFiles, setDocFiles] = useState({
    id_proof_file: null,
    license_file: null,
    photo_file: null
  });

  const [docData, setDocData] = useState(null);

  // Fetch Cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await publicAuth("cities", "GET");

        if (res?.data?.data) {
          setCities(res.data.data);
        }
      } catch (err) {
        console.error("Error loading cities:", err);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const res = await bearerAuth("timeslots", "GET");
        if (res?.data?.status && Array.isArray(res.data.data)) {
          const active = res.data.data.filter((s) => Number(s.status) === 1 || s.status === 1);
          const sorted = active.sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
          setTimeSlots(sorted);
        } else {
          setTimeSlots([]);
        }
      } catch (err) {
        console.error("Error loading time slots:", err);
        setTimeSlots([]);
      }
    };

    fetchSlots();
  }, []);

  useEffect(() => {
    const fetchWorkingHours = async () => {
      try {
        const res = await bearerAuth("driving-school/working-hours", "GET");
        const rows = res?.data?.data || [];
        if (!Array.isArray(rows) || rows.length === 0) {
          setHoursConfigured(false);
          return;
        }
        const dayOrder = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
        const normalized = dayOrder.map((day) => {
          const found = rows.find((r) => r.day_of_week === day);
          return found
            ? {
                day_of_week: found.day_of_week,
                opening_time: found.opening_time || "00:00:00",
                closing_time: found.closing_time || "00:00:00",
                status: found.status || "closed"
              }
            : { day_of_week: day, opening_time: "00:00:00", closing_time: "00:00:00", status: "closed" };
        });
        setWorkingHours(normalized);
        const anyOpen = normalized.some((h) => h.status === "open");
        setHoursConfigured(anyOpen);
      } catch (err) {
        console.error("Error loading working hours:", err);
      }
    };

    fetchWorkingHours();
  }, []);

  // Fetch documents
  const docsFetchedRef = useRef(false);
  useEffect(() => {
    if (docsFetchedRef.current) return;
    docsFetchedRef.current = true;

    const fetchDocs = async () => {
      setDocLoading(true);
      setDocError("");
      try {
        const res = await bearerAuth("driving-school/documents", "GET");
        const data = res?.data?.data || null;
        setDocData(data);
        if (data) {
          setDocForm({
            bank_name: data.bank_name || "",
            account_holder_name: data.account_holder_name || "",
            account_number: data.account_number || "",
            ifsc: data.ifsc || "",
            branch: data.branch || "",
            id_proof_type: data.id_proof_type || "",
            id_proof_number: data.id_proof_number || ""
          });
        }
      } catch (err) {
        setDocError(err?.message || "Failed to load documents");
      } finally {
        setDocLoading(false);
      }
    };

    fetchDocs();
  }, []);

  // Get City Name
  const getCityName = (cityId) => {
    const found = cities.find((c) => c.id == cityId);
    return found ? found.name : cityId;
  };

 // ✅ Correct Image Base URL
const imageBaseUrl = (process.env.REACT_APP_IMAGE_URL || "").replace(/\/+$/, "");

// ✅ Correct File URL Generator
const fileUrl = (path) => {
  if (!path) return "";

  // Agar already full URL hai to wahi return karo
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  // Clean starting slash
  const cleanPath = path.replace(/^\/+/, "");

  // Directly attach to IMAGE URL
  return `${imageBaseUrl}/${cleanPath}`;
};

  // Submit Update
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...form,
        zipcode: String(form.zipcode)
      };

      await bearerAuth("driving-school/edit", "POST", payload);

      updateUser(payload);

      // ✅ SweetAlert Success
      Swal.fire({
        title: "Success!",
        text: "Profile updated successfully!",
        icon: "success",
        timer: 1800,
        showConfirmButton: false
      });

      setShowEdit(false);

    } catch (err) {
      // ❌ SweetAlert Error
      Swal.fire({
        title: "Error!",
        text: err.message || "Something went wrong",
        icon: "error"
      });

    } finally {
      setLoading(false);
    }
  };

  const handleDocSubmit = async (e) => {
    e.preventDefault();
    setDocSaving(true);
    setDocError("");

    try {
      const formData = new FormData();
      Object.entries(docForm).forEach(([key, value]) => {
        formData.append(key, value || "");
      });

      Object.entries(docFiles).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });

      const res = await bearerAuth("driving-school/documents", "POST", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setDocData(res?.data?.data || null);
      setDocFiles({ id_proof_file: null, license_file: null, photo_file: null });

      Swal.fire({
        title: "Success!",
        text: "Documents updated successfully!",
        icon: "success",
        timer: 1800,
        showConfirmButton: false
      });
    } catch (err) {
      setDocError(err?.message || "Failed to update documents");
      Swal.fire({
        title: "Error!",
        text: err?.message || "Something went wrong",
        icon: "error"
      });
    } finally {
      setDocSaving(false);
    }
  };

  const handleWorkingHoursChange = (index, field, value) => {
    setWorkingHours((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === "status" && value === "closed") {
        next[index].opening_time = "00:00:00";
        next[index].closing_time = "00:00:00";
      }
      return next;
    });
  };

  const handleSaveWorkingHours = async () => {
    setHoursSaving(true);
    setHoursError("");
    try {
      const invalidOpen = workingHours.find(
        (h) => h.status === "open" && (!h.opening_time || !h.closing_time)
      );
      if (invalidOpen) {
        setHoursError(`Please select opening and closing time for ${invalidOpen.day_of_week}.`);
        Swal.fire({
          title: "Missing Time!",
          text: `Please select opening and closing time for ${invalidOpen.day_of_week}.`,
          icon: "warning"
        });
        setHoursSaving(false);
        return;
      }

      const payload = {
        working_hours: workingHours.map((h) => ({
          day_of_week: h.day_of_week,
          opening_time: h.status === "open" ? h.opening_time : "00:00:00",
          closing_time: h.status === "open" ? h.closing_time : "00:00:00",
          status: h.status
        }))
      };

      await bearerAuth("driving-school/set-working-hours", "POST", payload);
      setHoursConfigured(payload.working_hours.some((h) => h.status === "open"));
      Swal.fire({
        title: "Success!",
        text: "Working hours updated successfully!",
        icon: "success",
        timer: 1600,
        showConfirmButton: false
      });
    } catch (err) {
      setHoursError(err?.response?.data?.message || err?.message || "Failed to update working hours");
      Swal.fire({
        title: "Error!",
        text: err?.response?.data?.message || err?.message || "Failed to update working hours",
        icon: "error"
      });
    } finally {
      setHoursSaving(false);
    }
  };

  if (!user) {
    return <Alert variant="danger">User data not found!</Alert>;
  }

  return (
    <>
      <div className="profile-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">Driving School Profile</h2>
          <Button variant="primary" onClick={() => setShowEdit(true)}>
            Edit Profile
          </Button>
        </div>

        <div className="mb-3">
          <SchoolProfileProgress
            user={user}
            docData={docData}
            workingHours={workingHours}
            hoursConfigured={hoursConfigured}
          />
        </div>

        <Tabs defaultActiveKey="personal" id="ds-profile-tabs" className="mb-3">
          <Tab eventKey="personal" title="Personal Details">
            <Card>
              <Card.Header>
                <h5>School Details</h5>
              </Card.Header>

              <Card.Body>
                <ListGroup variant="flush">

                  <ListGroup.Item>
                    <Row>
                      <Col md={3} className="fw-bold">School Name:</Col>
                      <Col md={9}>{user.name}</Col>
                    </Row>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <Row>
                      <Col md={3} className="fw-bold">Email:</Col>
                      <Col md={9}>{user.email}</Col>
                    </Row>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <Row>
                      <Col md={3} className="fw-bold">Contact Number:</Col>
                      <Col md={9}>{user.contact_number}</Col>
                    </Row>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <Row>
                      <Col md={3} className="fw-bold">Address:</Col>
                      <Col md={9}>{user.address}</Col>
                    </Row>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <Row>
                      <Col md={3} className="fw-bold">City:</Col>
                      <Col md={9}>{getCityName(user.city_id)}</Col>
                    </Row>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <Row>
                      <Col md={3} className="fw-bold">Zip Code:</Col>
                      <Col md={9}>{user.zipcode}</Col>
                    </Row>
                  </ListGroup.Item>

                </ListGroup>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="working-hours" title="Working Hours">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Set Working Hours</h5>
                <Form.Check
                  type="checkbox"
                  id="applyToAllHours"
                  label="Same for all"
                  checked={applyToAll}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setApplyToAll(checked);
                    if (checked && workingHours.length) {
                      const first = workingHours[0];
                      setWorkingHours((prev) =>
                        prev.map((h) => ({
                          ...h,
                          opening_time: first.opening_time,
                          closing_time: first.closing_time,
                          status: first.status
                        }))
                      );
                    }
                  }}
                />
              </Card.Header>
              <Card.Body>
                {hoursError && <Alert variant="danger">{hoursError}</Alert>}

                <div className="table-responsive">
                  <table className="table table-bordered align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Day</th>
                        <th>Opening Time</th>
                        <th>Closing Time</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workingHours.map((day, index) => (
                        <tr key={day.day_of_week}>
                          <td>{day.day_of_week}</td>
                          <td>
                            <Form.Select
                              value={day.opening_time || ""}
                              onChange={(e) => {
                                const t = e.target.value;
                                handleWorkingHoursChange(index, "opening_time", t);
                                if (applyToAll) {
                                  setWorkingHours((prev) => prev.map((h) => ({ ...h, opening_time: t })));
                                }
                              }}
                              disabled={day.status === "closed"}
                            >
                              <option value="">Select Time</option>
                              {timeSlots.map((slot) => (
                                <option key={`open-${slot.id}`} value={String(slot.start_time).substring(0, 8)}>
                                  {String(slot.start_time).substring(0, 8)}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Select
                              value={day.closing_time || ""}
                              onChange={(e) => {
                                const t = e.target.value;
                                handleWorkingHoursChange(index, "closing_time", t);
                                if (applyToAll) {
                                  setWorkingHours((prev) => prev.map((h) => ({ ...h, closing_time: t })));
                                }
                              }}
                              disabled={day.status === "closed"}
                            >
                              <option value="">Select Time</option>
                              {timeSlots.map((slot) => (
                                <option key={`close-${slot.id}`} value={String(slot.end_time).substring(0, 8)}>
                                  {String(slot.end_time).substring(0, 8)}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Select
                              value={day.status}
                              onChange={(e) => {
                                const v = e.target.value;
                                handleWorkingHoursChange(index, "status", v);
                                if (applyToAll) {
                                  setWorkingHours((prev) =>
                                    prev.map((h) => ({
                                      ...h,
                                      status: v,
                                      opening_time: v === "closed" ? "00:00:00" : prev[0].opening_time,
                                      closing_time: v === "closed" ? "00:00:00" : prev[0].closing_time
                                    }))
                                  );
                                }
                              }}
                            >
                              <option value="open">Open</option>
                              <option value="closed">Closed</option>
                            </Form.Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="text-end">
                  <Button variant="primary" onClick={handleSaveWorkingHours} disabled={hoursSaving}>
                    {hoursSaving ? "Saving..." : "Save Working Hours"}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="documents" title="Documents Details">
            <Card>
              <Card.Header>
                <h5>Documents</h5>
              </Card.Header>
              <Card.Body>
                {docLoading && <Alert variant="info">Loading documents...</Alert>}
                {docError && <Alert variant="danger">{docError}</Alert>}

                <Form onSubmit={handleDocSubmit}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Bank Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={docForm.bank_name}
                          onChange={(e) => setDocForm({ ...docForm, bank_name: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Account Holder Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={docForm.account_holder_name}
                          onChange={(e) => setDocForm({ ...docForm, account_holder_name: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Account Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={docForm.account_number}
                          onChange={(e) => setDocForm({ ...docForm, account_number: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>IFSC</Form.Label>
                        <Form.Control
                          type="text"
                          value={docForm.ifsc}
                          onChange={(e) => setDocForm({ ...docForm, ifsc: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Branch</Form.Label>
                        <Form.Control
                          type="text"
                          value={docForm.branch}
                          onChange={(e) => setDocForm({ ...docForm, branch: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>ID Proof Type</Form.Label>
                        <Form.Control
                          type="text"
                          value={docForm.id_proof_type}
                          onChange={(e) => setDocForm({ ...docForm, id_proof_type: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>ID Proof Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={docForm.id_proof_number}
                          onChange={(e) => setDocForm({ ...docForm, id_proof_number: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>ID Proof File (JPG/PNG/PDF)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => setDocFiles({ ...docFiles, id_proof_file: e.target.files?.[0] || null })}
                        />
                        {docData?.id_proof_file && (
                          <div className="mt-1">
                            <a href={fileUrl(docData.id_proof_file)} target="_blank" rel="noreferrer">
                              View current file
                            </a>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Driving School License (JPG/PNG/PDF)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => setDocFiles({ ...docFiles, license_file: e.target.files?.[0] || null })}
                        />
                        {docData?.license_file && (
                          <div className="mt-1">
                            <a href={fileUrl(docData.license_file)} target="_blank" rel="noreferrer">
                              View current file
                            </a>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Photo (JPG/PNG)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={(e) => setDocFiles({ ...docFiles, photo_file: e.target.files?.[0] || null })}
                        />
                        {docData?.photo_file && (
                          <div className="mt-1">
                            <a href={fileUrl(docData.photo_file)} target="_blank" rel="noreferrer">
                              View current file
                            </a>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    <Button type="submit" variant="primary" disabled={docSaving}>
                      {docSaving ? "Saving..." : "Save Documents"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="security" title="Security">
            <div className="mt-3">
              <ChangePasswordCard endpoint="driving-school/change-password" />
            </div>
          </Tab>
        </Tabs>
      </div>

      {/* EDIT MODAL */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Driving School</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>School Name</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select City</Form.Label>
              <Form.Select
                value={form.city_id}
                onChange={(e) => setForm({ ...form, city_id: e.target.value })}
                required
              >
                <option value="">Select City</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name} ({city.state_name})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Zipcode</Form.Label>
              <Form.Control
                type="text"
                value={form.zipcode}
                onChange={(e) => setForm({ ...form, zipcode: e.target.value })}
                required
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ProfileDs;
